import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getIntlCommittees,
  sendCommitteeMessage,
} from '../../../endpoints/chapters';
import PositionDisplay from '../../../Components/PositionDisplay';
import {
  InternationalCommitteeResponse,
  InternationalCommitteeMessage,
} from '../../../types/chapters';

const IntlCommittees: React.FC = () => {
  let [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [errorSaving, setErrorSaving] = useState<string | null>(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filteredCommittees, setFilteredCommittees] = useState<
    InternationalCommitteeResponse[]
  >([]);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedCommittee, setSelectedCommittee] =
    useState<InternationalCommitteeResponse | null>(null);
  const [messageText, setMessageText] = useState('');

  const { data: committeesData, isFetching } = useApiGet<
    InternationalCommitteeResponse[]
  >(
    ['intl-committees'],
    getIntlCommittees,
    {
      enabled: true,
      retry: 1,
    },
    {}
  );

  const { mutate: sendMessage, isPending: isSendingMessage } = useApiSend(
    sendCommitteeMessage,
    () => {
      setMessageDialogOpen(false);
      setMessageText('');
      setSelectedCommittee(null);
      // Show success message
    },
    (error) => {
      setErrorSaving(error?.message || 'Error sending message');
    }
  );

  const handleSearch = (search: string) => {
    setSearch(search);
  };

  const handleMessageClick = (committee: InternationalCommitteeResponse) => {
    setSelectedCommittee(committee);
    setMessageDialogOpen(true);
  };

  const handleSendMessage = () => {
    if (selectedCommittee && messageText.trim()) {
      const messageData: InternationalCommitteeMessage = {
        committee_id: selectedCommittee.id,
        user_id: null,
        message: messageText.trim(),
      };
      sendMessage(messageData);
    }
  };

  const handleCloseMessageDialog = () => {
    setMessageDialogOpen(false);
    setMessageText('');
    setSelectedCommittee(null);
  };

  useEffect(() => {
    if (committeesData) {
      setFilteredCommittees(
        committeesData.filter((committee) =>
          committee.name.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [committeesData, search]);

  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!committeesData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} sx={{ p: 2 }}>
        <TextField
          label="Search"
          id="search-field"
          size="small"
          value={search}
          fullWidth={true}
          onChange={(event) => handleSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      {filteredCommittees.map((committee) => (
        <Grid item xs={12} md={6} sx={{ p: 2 }} key={committee.id}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6">{committee.name}</Typography>
                  {committee.is_project && (
                    <Button
                      onClick={() => handleMessageClick(committee)}
                      variant="contained"
                      size="small"
                      sx={{
                        fontWeight: 'bold',
                        pb: 0,
                        pt: 0,
                        pl: 1,
                        pr: 1,
                        ml: 1,
                      }}
                    >
                      Message
                    </Button>
                  )}
                </Box>
              }
              subheader={committee.description}
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                {committee.members
                  .sort((a, b) => a.order - b.order)
                  .map((user) => (
                    <PositionDisplay
                      id={user.id}
                      position={user.position}
                      positionId={user.position_id}
                      committeeId={user.committee_id}
                      name={user.name}
                      startDate={user.start_date}
                      endDate={user.end_date}
                      canEdit={false} // canEdit()} // Uncomment if you want to allow editing
                      canMessage={!committee.is_project} // Set canMessage to false if is_project is true
                      isSaving={false}
                      errorSavingText={errorSaving}
                      onSave={null} // onSavePosition}
                      userId={user.user_id}
                      pageFrom={'intl-committees'}
                      key={user.id}
                    />
                  ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* Message Dialog */}
      <Dialog
        open={messageDialogOpen}
        onClose={handleCloseMessageDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Message to {selectedCommittee?.name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Enter your message..."
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMessageDialog}>Cancel</Button>
          <Button
            onClick={handleSendMessage}
            variant="contained"
            disabled={!messageText.trim() || isSendingMessage}
          >
            {isSendingMessage ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default IntlCommittees;
